import { immutableJSONPatch, parseJSONPointer, type JSONPatchOperation } from 'immutable-json-patch'
import { isAlias, isMap, isNode, isScalar, isSeq, parseDocument, visit, type Document, type Node } from 'yaml'

export type StructuredValue = null | boolean | number | string | StructuredValue[] | { [key: string]: StructuredValue }
export type StructuredView = { kind: 'editable'; value: StructuredValue }
  | { kind: 'invalid' | 'unsupported'; message: string }

class UnsupportedStructure extends Error {}
const UNSUPPORTED = '此内容暂不能安全使用结构树编辑，请切换源码'
const unsupported = (): never => { throw new UnsupportedStructure(UNSUPPORTED) }

/** Normalize only JSON-representable values; never round unsafe integers into a tree projection. */
const jsonValue = (value: unknown): StructuredValue => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'bigint') {
    if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) return unsupported()
    return Number(value)
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value))) return unsupported()
    return value
  }
  if (Array.isArray(value)) return value.map(jsonValue)
  if (typeof value !== 'object' || (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null)) return unsupported()
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, jsonValue(item)]))
}

const documentOf = (content: string): Document => {
  const doc = parseDocument(content, { keepSourceTokens: true, intAsBigInt: true })
  if (doc.errors.length) throw new Error(doc.errors[0].message)
  if (doc.warnings.length) return unsupported()
  visit(doc, {
    Node: (_key, node) => {
      if (isAlias(node) || node.tag || ('anchor' in node && node.anchor)) unsupported()
    },
    Pair: (_key, pair) => {
      if (!isScalar(pair.key) || typeof pair.key.value !== 'string') unsupported()
    },
  })
  jsonValue(doc.toJS({ maxAliasCount: 0 }))
  return doc
}

export const structuredView = (content: string): StructuredView => {
  try { return { kind: 'editable', value: jsonValue(documentOf(content).toJS({ maxAliasCount: 0 })) } }
  catch (error) { return { kind: error instanceof UnsupportedStructure ? 'unsupported' : 'invalid', message: (error as Error).message } }
}

const pointer = (value: string): string[] => {
  const path = parseJSONPointer(value).map(String)
  if (path.some((part) => ['__proto__', 'prototype', 'constructor'].includes(part))) throw new Error('此字段路径不可修改')
  return path
}

const clonedAt = (doc: Document, path: string[]): Node => {
  const value = doc.getIn(path, true)
  const cloned = isNode(value) ? value.clone() : doc.createNode(value)
  if (!isNode(cloned)) throw new Error('无法复制此节点')
  const parent = doc.getIn(path.slice(0, -1), true)
  const pair = isMap(parent) ? parent.items.find(({ key }) => isScalar(key) && key.value === path[path.length - 1]) : undefined
  if (isScalar(pair?.key) && pair.key.commentBefore) cloned.commentBefore = [pair.key.commentBefore, cloned.commentBefore].filter(Boolean).join('\n')
  return cloned
}

const replacement = (doc: Document, path: string[], value: unknown): Node => {
  const next = doc.createNode(jsonValue(value))
  const previous = doc.getIn(path, true)
  if (!isNode(previous)) return next
  next.comment = previous.comment; next.commentBefore = previous.commentBefore; next.spaceBefore = previous.spaceBefore
  if (isScalar(previous) && isScalar(next) && typeof previous.value === 'string' && typeof next.value === 'string') next.type = previous.type
  return next
}

const put = (doc: Document, path: string[], value: Node, insert: boolean): void => {
  if (!path.length) { doc.contents = value; return }
  const parent = doc.getIn(path.slice(0, -1), true)
  const key = path[path.length - 1]
  if (isSeq(parent) && insert) {
    parent.items.splice(key === '-' ? parent.items.length : Number(key), 0, value)
    return
  }
  if (!isMap(parent) && !isSeq(parent)) throw new Error('目标字段已不存在')
  parent.set(key, value)
}

type Edit = (doc: Document, operation: JSONPatchOperation) => void
const edits: Record<JSONPatchOperation['op'], Edit> = {
  add: (doc, operation) => {
    if (!('value' in operation)) return
    const path = pointer(operation.path)
    put(doc, path, replacement(doc, path, operation.value), true)
  },
  replace: (doc, operation) => {
    if (!('value' in operation)) return
    const path = pointer(operation.path)
    put(doc, path, replacement(doc, path, operation.value), false)
  },
  remove: (doc, operation) => { doc.deleteIn(pointer(operation.path)) },
  copy: (doc, operation) => {
    if (!('from' in operation)) return
    put(doc, pointer(operation.path), clonedAt(doc, pointer(operation.from)), true)
  },
  move: (doc, operation) => {
    if (!('from' in operation) || operation.from === operation.path) return
    const from = pointer(operation.from)
    const value = clonedAt(doc, from)
    doc.deleteIn(from)
    put(doc, pointer(operation.path), value, true)
  },
  test: () => undefined,
}

const jsonStyle = (content: string) => {
  try { JSON.parse(content); return true }
  catch { return false }
}

/** Let the mature JSON Patch engine validate operations, while YAML AST owns stored text. */
export const editStructured = (content: string, operations: readonly JSONPatchOperation[]): string => {
  const doc = documentOf(content)
  const original = jsonValue(doc.toJS({ maxAliasCount: 0 }))
  const updated = immutableJSONPatch(original, [...operations], {
    before: (_value, operation) => { pointer(operation.path); edits[operation.op](doc, operation); return {} },
  })
  const expected = jsonValue(updated)
  if (JSON.stringify(original) === JSON.stringify(expected)) return content
  if (JSON.stringify(jsonValue(doc.toJS({ maxAliasCount: 0 }))) !== JSON.stringify(expected)) throw new Error('无法安全更新结构，请切换源码')
  const options = jsonStyle(content) ? { lineWidth: 0, defaultStringType: 'QUOTE_DOUBLE' as const, defaultKeyType: 'QUOTE_DOUBLE' as const } : { lineWidth: 0 }
  const result = doc.toString(options)
  const accepted = structuredView(result)
  if (accepted.kind !== 'editable' || JSON.stringify(accepted.value) !== JSON.stringify(expected)) throw new Error('无法安全更新结构，请切换源码')
  return result
}
